import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { X, Coins } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { playGiftSound, unlockAudio } from "../lib/giftSounds";
import { showGoldlyReward } from "./GoldlyReward";
import GiftFlash from "./GiftFlash";

export default function GiftSheet({ reel, onClose }) {
  const { user, refresh } = useAuth();
  const [gifts, setGifts] = useState([]);
  const [sending, setSending] = useState(null);
  const [msg, setMsg] = useState("");
  const [flash, setFlash] = useState(null);

  useEffect(() => { api.get("/gifts").then(r => setGifts(r.data)); }, []);

  const sendGift = (g) => {
    // CRITICAL: unlock audio synchronously inside the click handler before async work
    unlockAudio();
    if (sending) return;
    setSending(g.id); setMsg("");
    setFlash(g);
    playGiftSound(g);
    api.post("/gifts/send", { reel_id: reel.id, gift_id: g.id })
      .then(() => {
        setMsg(`Sent ${g.name}! ${g.emoji}`);
        refresh();
        // Auto-close after a brief celebration so the gift sheet doesn't block the reel
        setTimeout(() => { setMsg(""); onClose?.(); }, 900);
      })
      .catch(e => {
        setMsg(e?.response?.data?.detail || "Failed to send");
        setTimeout(()=>setMsg(""), 2000);
      })
      .finally(() => setSending(null));
  };

  return (
    <>
      {flash && <GiftFlash gift={flash} onDone={() => setFlash(null)} />}
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-50 max-w-md mx-auto" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
        <div className="absolute inset-0 bg-black/70" onClick={onClose} />
        <motion.div initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring", damping:30}}
          className="absolute bottom-0 inset-x-0 glass rounded-t-3xl max-h-[80vh] flex flex-col" data-testid="gift-sheet">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h3 className="font-outfit font-bold text-lg">Send a Gift 🎁</h3>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-[#FF7A00] font-bold text-sm" data-testid="gift-balance">
                <Coins className="w-4 h-4" />{user?.wallet_goldly ?? 0}
              </span>
              <button onClick={onClose} data-testid="gift-close"><X className="w-6 h-6"/></button>
            </div>
          </div>
          {msg && <div className="px-4 py-2 text-center text-sm text-[#00E5FF]" data-testid="gift-msg">{msg}</div>}
          <div className="overflow-y-auto p-3 grid grid-cols-4 gap-2 scrollbar-hide">
            {gifts.map(g => (
              <button key={g.id} onClick={()=>sendGift(g)} disabled={sending===g.id}
                data-testid={`gift-${g.id}`}
                className="flex flex-col items-center p-2 rounded-2xl bg-white/5 border border-white/10 active:scale-95 transition disabled:opacity-50">
                <div className="text-3xl">{g.emoji}</div>
                <div className="text-[10px] mt-1 truncate w-full text-center text-white/80">{g.name}</div>
                <div className="text-[11px] text-[#FF7A00] font-bold flex items-center gap-0.5 mt-0.5">
                  <Coins className="w-3 h-3" />{g.price}
                </div>
              </button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
    </>
  );
}
